// Copyright (c) Microsoft. All rights reserved.

import { AnyAction, Dispatch, MiddlewareAPI, MiddlewareArray, ThunkMiddleware, configureStore } from '@reduxjs/toolkit';
import { AppState } from '../features/app/AppState';
import { ConfigState } from '../features/config/ConfigState';
import { ConversationsState } from '../features/conversations/ConversationsState';
import { signalRMiddleware } from '../features/message-relay/signalRMiddleware';
import { PluginsState } from '../features/plugins/PluginsState';
import { UsersState } from '../features/users/UsersState';
import { TeamsState } from '../teams/TeamsState';
import resetStateReducer, { resetApp } from './rootReducer';

export type StoreMiddlewareAPI = MiddlewareAPI<Dispatch, RootState>;
export type Store = typeof store;
export const store = configureStore<
    RootState,
    AnyAction,
    MiddlewareArray<
        [ThunkMiddleware<RootState>, (store: StoreMiddlewareAPI) => (next: Dispatch) => (action: any) => any]
    >
>({
    reducer: resetStateReducer,
    // serializableCheck: false is required to allow the store to accept the TeamsCredentials, which are not serializable
    middleware: (getDefaultMiddleware) => getDefaultMiddleware({ serializableCheck: false }).concat(signalRMiddleware),
});

export interface RootState {
    app: AppState;
    conversations: ConversationsState;
    plugins: PluginsState;
    users: UsersState;
    config: ConfigState;
    teams: TeamsState;
}

export const getSelectedChatID = (): string => {
    return store.getState().conversations.selectedId;
};

export type AppDispatch = typeof store.dispatch;

// Function to reset the app state
export const resetState = () => {
    store.dispatch(resetApp());
};
